<template>
  <div class="c-blogContent">
    <!-- カバー画像 -->
    <div
      class="c-blogContent_img"
      v-if="blog.coverImageUrl"
    >
      <img
        :src="blog.coverImageUrl"
        fetchpriority="high"
      />
    </div>
    <!-- タイトル表示 -->
    <div>
      <h1 class="c-blogContent_title">{{ blog.title }}</h1>
      <span class="c-blogContent_kind">
        <coconala-icon
          :name="kindIconName"
          :size="kindIconSize"
        />
        {{ kindText }}
      </span>
    </div>
    <div class="c-blogContent_category">{{ blog.blogCategory.name }}</div>
    <span
      v-if="blog.paidFg && !isFromCoconalaApp && blog.paidOrdersCount"
      class="c-blogContent_orderCount c-blogContent_orderCount-sp"
    >{{ blog.paidOrdersCount }}人が購入しています</span>
    <!-- ユーザー情報 -->
    <div class="c-blogContent_head">
      <component
        :is="isSnapshot ? 'div' : 'nuxt-link'"
        :to="`/blogs/${user.id}`"
        class="c-blogContent_writer"
      >
        <c-user-icon-v2
          :file-path="isSnapshot ? user.userIconPath : user.resizableProfileIcon"
          :size="isPC ? 40 : 24"
        />
        <div class="c-blogContent_writerDetail">
          <div class="c-blogContent_writerName">{{ user.name }}</div>
          <div class="c-blogContent_postDate">{{ $util.dayjs.unix(blog.openedAt).format('YYYY/MM/DD HH:mm') }}</div>
        </div>
      </component>
      <template v-if="isOwner">
        <div>
          <span
            class="c-blogContent_myBlogPrice"
            v-if="blog.paidFg"
          >{{ $util.numberWithDelimiter(blog.price) }}円</span>
          <nuxt-link
            v-if="isPC"
            :to="`/mypage/blogs/edit/${blog.id}`"
          >
            <coconala-icon name="pencil-square-o" />
            編集する
          </nuxt-link>
        </div>
      </template>
      <div v-else-if="!isOwner">
        <span
          v-if="isPaidBlogPurchased || isFreeBlogPurchased"
          class="c-blogContent_purchased"
        >購入済み</span>
        <template v-else-if="blog.paidFg && !isFromCoconalaApp">
          <span
            class="c-blogContent_orderCount c-blogContent_orderCount-pc"
            v-if="blog.paidOrdersCount"
          >{{ blog.paidOrdersCount }}人が購入しています</span>
          <CButtonV2
            width="100px"
            color="primary"
            no-shadow
            size="s"
            @click="goToPayment"
          >{{ $util.numberWithDelimiter(blog.price) }}円</CButtonV2>
        </template>
      </div>
    </div>
    <!-- 記事表示 -->
    <v-runtime-template
      class="c-blogBody"
      :class="{ 'c-blogBody-paidBlog': blog.paidFg }"
      :template="`<div>${body ? $sanitize(body, sanitizeOptions) : $sanitize(blog.body, sanitizeOptions)}</div>`"
    />
    <!-- 有料記事の販促ボタン -->
    <blog-sales-promote
      v-if="blog.paidFg && !isOwner && !isPaidBlogPurchased"
      @goToPayment="goToPayment"
    />
    <!-- 自分の有料ブログの場合、有料エリアを表示する区切り線を表示する -->
    <template v-if="isMyPaidBlog">
      <div class="c-blogContent_dividingPayLine">
        <div class="c-blogContent_dividingPayLineBorder"></div>
        <div class="c-blogContent_dividingPayLineBlock">このラインから下を、有料設定しています</div>
      </div>
      <div
        v-if="!isPC"
        class="c-blogContent_ownerRemarkSp"
      >
        ブログの投稿・編集・設定はパソコン<br>
        から行えます
      </div>
    </template>
    <!-- 有料記事エリア表示 -->
    <v-runtime-template
      class="c-blogBody"
      :template="`<div>${paidBody ? $sanitize(paidBody, sanitizeOptions) : $sanitize(blog.paidBody, sanitizeOptions)}</div>`"
    />
    <!-- ハッシュタグ -->
    <div class="c-blogContent_hashTag">
      <a
        v-for="(tag, index) in blog.blogTagsList"
        :key="index"
        :href="`/blogs/search?blogTagId=${tag.id}`"
      >#{{ tag.name }}</a>
    </div>
    <!-- SNSリンク -->
    <div
      v-if="!isSnapshot"
      class="c-blogContent_sns"
    >
      <client-only>
        <a
          class="c-blogContent_favBtn"
          :class="{ 'c-blogContent_favBtn-favorited': isFavorited }"
          href="javascript:;"
          @click.prevent="toggleFavorite"
        >
          <coconala-icon
            name="heart"
            size="25px"
          />
          <span class="c-blogContent_favCount">
            {{ favoriteCount }}
          </span>
        </a>
        <share-links
          :title="`${blog.title}｜${user.name}`"
          :path="`/blogs/${user.id}/${blog.id}`"
          :hashtags="getSocialSharingTags()"
        />
      </client-only>
    </div>
    <!-- プロフィール表示下部(アイコン/名前/詳細情報) -->
    <blog-profile v-if="!isSnapshot" />
    <!-- 別記事誘導（スライダー) -->
    <blog-pagination v-if="!isSnapshot" />
    <!-- Appの場合、ブログ購入ができないためそのためのアラート -->
    <ClientOnly>
      <blog-purchase-alert-for-app
        :is-visible="isVisibleBlogPurchaseAlertForApp"
        @close="isVisibleBlogPurchaseAlertForApp = false"
      />
    </ClientOnly>
  </div>
</template>
<script>
import BlogProfile from './components/BlogProfile'
import BlogPagination from './components/BlogPagination'
import BlogService from './components/BlogService'
import BlogWidget from './components/BlogWidget'
import BlogSalesPromote from './components/BlogSalesPromote'
import BlogPurchaseAlertForApp from './components/BlogPurchaseAlertForApp'
import VRuntimeTemplate from 'v-runtime-template'

// vuex
import { mapGetters, mapState, mapActions } from 'vuex'

// import sanitize-html
import Vue from 'vue'
import sanitizeHTML from 'sanitize-html'

Vue.prototype.$sanitize = sanitizeHTML

const sanitizeOptions = {
  allowedTags: [
    'div',
    'blockquote',
    'h2',
    'img',
    'b',
    'br',
    'hr',
    'iframe',
    'a',
    'blog-service',
    'blog-widget',
    'blog-table-of-contents'
  ],
  allowedClasses: {
    div: [
      'c-blogBody_service',
      'c-blogBody_widget',
      'c-blogBody_text',
      'c-blogBody_center',
      'c-blogBody_youtube',
      'c-blogBody_img',
      'c-blogBody_insertLink'
    ],
    h2: ['c-blogBody_h2', 'c-blogBody_center'],
    blockquote: ['c-blogBody_blockquote', 'c-blogBody_center']
  },
  allowedAttributes: {
    h2: ['id'],
    iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen'],
    img: ['src', 'alt', 'loading'],
    a: ['href', 'name', 'target', 'rel'],
    'blog-service': ['id', 'title'],
    'blog-widget': ['blog-id', 'user-id', 'title'],
    'blog-table-of-contents': ['blog-table-of-contents']
  },
  allowedIframeHostnames: ['www.youtube.com']
}

export default {
  name: 'BlogContent',
  props: {
    isSnapshot: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BlogProfile,
    BlogPagination,
    BlogService,
    BlogWidget,
    BlogSalesPromote,
    BlogPurchaseAlertForApp,
    VRuntimeTemplate,
    BlogTableOfContents: () =>
      import('./components/BlogTableOfContents' /* webpackChunkName: "BlogTableOfContents" */),
    ShareLinks: () =>
      import('~/components/organisms/ShareLinks' /* webpackChunkName: "ShareLinks" */)
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'isFromCoconalaApp']),
    ...mapGetters('ui', ['isPC']),
    ...mapGetters('pages/blogs/user/blog', ['getSocialSharingTags']),
    ...mapState('pages/blogs/user', ['services']),
    ...mapState('pages/blogs/order', ['blogOrder']),
    ...mapState('pages/blogs/constants', ['KIND', 'ORDER_STATUS']),
    ...mapState('constants', ['COCONALA_WHITE_LIST_URL']),
    ...mapGetters('pages/users', ['isOwner']),
    blog() {
      return this.isSnapshot
        ? this.$store.state.pages.blogs.snapshot.blogSnapshot
        : this.$store.state.pages.blogs.user.blog.blog
    },
    isKindNormal() {
      return this.blog.kind === this.KIND.NORMAL
    },
    isKindContents() {
      return this.blog.kind === this.KIND.CONTENTS
    },
    isKindAnnounce() {
      return this.blog.kind === this.KIND.ANNOUNCE
    },
    kindIconName() {
      if (this.isKindNormal) return 'blog-article'
      if (this.isKindContents) return 'blog-content'
      if (this.isKindAnnounce) return 'blog-announce'
      return 'blog-article'
    },
    kindIconSize() {
      if (this.isKindNormal) return '12px'
      if (this.isKindContents) return '12px'
      if (this.isKindAnnounce) return '11px'
      return '12px'
    },
    kindText() {
      return this.$store.getters['pages/blogs/constants/kindText'](this.blog.kind)
    },
    user() {
      return this.isSnapshot
        ? this.$store.state.pages.blogs.snapshot.userProfile
        : this.$store.state.pages.users.user
    },
    isMyPaidBlog() {
      return this.blog.paidFg && this.isOwner
    },
    isPurchased() {
      return this.blogOrder && this.blogOrder.status === this.ORDER_STATUS.ENABLE
    },
    isPaidBlogPurchased() {
      return this.isSnapshot || (this.blog.paidFg && this.isPurchased && this.blog.paidBody !== '')
    },
    // 出品者が価格変更などを行えるため、無料でも購入済み状態を判定して「購入済み」ラベルを表示するために使用
    isFreeBlogPurchased() {
      return !this.blog.paidFg && this.isPurchased
    }
  },
  data() {
    return {
      body: '',
      paidBody: '',
      sanitizeOptions,
      isVisibleBlogPurchaseAlertForApp: false,
      isFavorited: false,
      favoriteCount: 0,
      generateTableOfContentsTimer: null
    }
  },
  watch: {
    isPurchased() {
      const tocDom = document.getElementsByClassName('js-blogTableOfContents_inner')
      if (!tocDom.length && this.blog.tocAvailable) {
        let counter = 1
        this.generateTableOfContentsTimer = setInterval(() => {
          if (this.blog.paidBody || counter > 100) {
            this.generateTableOfContents()
            clearInterval(this.generateTableOfContentsTimer)
          }
          counter++
        }, 100)
      }
    }
  },
  created() {
    this.coconalaWhiteListAutoLink()
  },
  mounted() {
    this.isFavorited = this.blog.isFavorited
    this.favoriteCount = this.blog.favoriteCount

    if (this.blog.tocAvailable) {
      this.generateTableOfContents()
    }
  },
  methods: {
    ...mapActions('ui', ['showLoginModal']),
    ...mapActions('pages/mypage/favorites/blogs', ['addFavorite', 'deleteFavorite']),
    /**
     * ブログ詳細表示時に特定のURLのみリンク化する
     * 対象URLは下記参照
     * https://docs.google.com/spreadsheets/d/1GtCxl_eZMoHWTagYSoA1FOhFiFc4IKWhhG3zHLLqOh0/edit#gid=369939209
     */
    coconalaWhiteListAutoLink() {
      const WHITE_LIST_URL_PATTERN = Object.keys(this.COCONALA_WHITE_LIST_URL)
        .map(key => {
          return this.COCONALA_WHITE_LIST_URL[key]
        })
        .join('|')
      const reg = new RegExp(`(${WHITE_LIST_URL_PATTERN})`, 'g')
      this.body = this.blog.body.replace(reg, match => {
        // ブラウザのエディタによって(Edege等)半角スペースがリンクに混ぜ込む事があるので削除する
        const link = match.trim().replace(/&nbsp;/, '')
        return `<a target="_blank" rel="noopener" href="${link}">${link}</a>`
      })
      this.paidBody = this.blog.paidBody.replace(reg, match => {
        // ブラウザのエディタによって(Edege等)半角スペースがリンクに混ぜ込む事があるので削除する
        const link = match.trim().replace(/&nbsp;/, '')
        return `<a target="_blank" rel="noopener" href="${link}">${link}</a>`
      })
    },
    generateTableOfContents() {
      const parser = new DOMParser()
      const body = this.body ? this.body : this.blog.body
      const freeAreaDoc = parser.parseFromString(body, 'text/html')
      const freeAreaHeadlinesDom = freeAreaDoc.querySelectorAll('.c-blogBody_h2')
      const paidBody = this.paidBody ? this.paidBody : this.blog.paidBody
      const paidAreaDoc = parser.parseFromString(paidBody, 'text/html')
      const paidAreaHeadlinesDom = paidAreaDoc.querySelectorAll('.c-blogBody_h2')
      // 見出しから目次を作成v2（v1のデータクレンジングができるまでは共存）
      let blogTableOfContents = []
      let blogTableOfContentsVersion = 'v2'
      if (this.blog.tocList.length) {
        // 目次作成（tocがあるときの処理）
        blogTableOfContents = this.blog.tocList
      } else {
        // 見出しが存在しない場合は目次を表示しない
        if (!freeAreaHeadlinesDom.length && !paidAreaHeadlinesDom.length) {
          return
        }
        // 目次作成（v1で作成またはtocがないときの処理）
        blogTableOfContentsVersion = 'v1'
        // 無料部分の目次作成
        Array.prototype.forEach.call(freeAreaHeadlinesDom, (freeAreaHeadlineDom, index) => {
          // 見出しタグに文字がなかった場合は除外
          if (!freeAreaHeadlineDom.textContent.length) {
            return
          }
          const id = `freeHeadline${index}`
          blogTableOfContents.push({
            title: freeAreaHeadlineDom.textContent,
            id
          })
          freeAreaHeadlineDom.setAttribute('id', id)
        })

        // 有料部分の目次作成
        if (this.isPaidBlogPurchased || this.isMyPaidBlog) {
          Array.prototype.forEach.call(paidAreaHeadlinesDom, (paidAreaHeadlineDom, index) => {
            // 見出しタグに文字がなかった場合は除外
            if (!paidAreaHeadlineDom.textContent.length) {
              return
            }
            const id = `paidHeadline${index}`
            blogTableOfContents.push({
              title: paidAreaHeadlineDom.textContent,
              id
            })
            paidAreaHeadlineDom.setAttribute('id', id)
          })
        }
        // 空文字の見出しタグしかなかった場合は目次を表示しない
        if (!blogTableOfContents.length) {
          return
        }
      }
      // 目次用のコンポーネントを最初の見出しの上に挿入
      const blogTableOfContentsTag = document.createElement('blog-table-of-contents')
      // 1つしかpropsで渡せないので1つのJSONにする
      const blogTableOfContentsJson = {
        tocList: blogTableOfContents,
        isPaidBlogPurchased: this.isPaidBlogPurchased,
        isMyPaidBlog: this.isMyPaidBlog,
        version: blogTableOfContentsVersion
      }
      // Valueはv-runtime-templateを使用するため、Stringにする
      blogTableOfContentsTag.setAttribute(
        'blog-table-of-contents',
        JSON.stringify(blogTableOfContentsJson)
      )
      // 無料エリア側に見出しがあれば無料エリア側に設定
      // 有料エリア側からしか見出しが設定されていなければ有料エリア側に設定
      if (freeAreaHeadlinesDom.length) {
        freeAreaHeadlinesDom[0].parentNode.insertBefore(
          blogTableOfContentsTag,
          freeAreaHeadlinesDom[0]
        )
      } else if (paidAreaHeadlinesDom.length && (this.isPaidBlogPurchased || this.isMyPaidBlog)) {
        paidAreaHeadlinesDom[0].parentNode.insertBefore(
          blogTableOfContentsTag,
          paidAreaHeadlinesDom[0]
        )
      }
      // 本文を塗り替え
      this.body = freeAreaDoc.body.innerHTML
      // 有料記事がある場合だけ塗り替える
      if (paidBody) {
        this.paidBody = paidAreaDoc.body.innerHTML
      }
    },
    async goToPayment(e) {
      // Appなら購入できないためアラート表示
      if (this.isFromCoconalaApp) {
        this.isVisibleBlogPurchaseAlertForApp = true
        return
      }
      // 未ログインならログインモーダル表示
      if (!this.isLoggedIn) {
        e.preventDefault()
        return this.showLoginModal()
      }
      try {
        // 購入制限チェック
        const response = await this.$store.dispatch('auth/user/getBlackList')
        if (response.notOrderFg) {
          this.$notify({
            group: 'flash',
            title: '現在ブログを購入できません',
            type: 'error-text-center'
          })
          return
        }
        // 購入状態確認
        await this.$store.dispatch('pages/blogs/order/fetchBlogOrder', this.$route.params.blogId)
        if (this.isPaidBlogPurchased) {
          // すでに購入しているため、リロードする
          this.$router.go({ path: this.$router.currentRoute.path, force: true })
          return
        }
        // 購入モーダル表示時に決済中フラグを立てる
        this.$store.commit('pages/blogs/user/blog/SET_IS_PAYMENT_IN_PROGRESS', true)
        this.$router.push({ name: 'blogs-id-blogId-input' }, () => {})
      } catch ({ grpcCode, message }) {
        const title = this.$util.getNotifyErrorTitle(grpcCode, message)
        const text = this.$util.getNotifyErrorText(grpcCode, message)
        this.$notify({
          group: 'flash',
          title,
          text,
          type: 'error',
          max: 1
        })
        return
      }
    },
    async toggleFavorite() {
      const blogId = this.$route.params.blogId
      // 未ログイン時はログインモーダルを表示する
      if (!this.isLoggedIn) {
        this.showLoginModal()
        return
      }

      if (!this.isFavorited) {
        await this.addFavorite({ blogId })
          .then(() => {
            // 表示上のカウント数はトグルで増減させ、正確な集計値はバックエンドで計算する
            this.favoriteCount++
            this.isFavorited = true
          })
          .catch(error => {
            this.$notify({
              group: 'flash',
              title: 'お気に入りを追加できませんでした',
              type: 'error'
            })
          })
      } else {
        await this.deleteFavorite({ blogId })
          .then(() => {
            this.favoriteCount--
            this.isFavorited = false
          })
          .catch(() => {
            this.$notify({
              group: 'flash',
              title: 'お気に入りを削除できませんでした',
              type: 'error'
            })
          })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.c-blogContent {
  &_head {
    display: flex;
    margin-bottom: 40px;

    align-items: center;
    justify-content: space-between;

    a {
      @include link-effect;
    }
  }

  &_writer {
    display: flex;
    margin-bottom: 12px;

    align-items: center;
  }

  &_writerDetail {
    margin-left: 8px;
  }

  &_writerName {
    color: $color-gray-900;
    font-size: 16px;
  }

  &_postDate {
    color: $color-gray-600;
    font-size: 11px;
  }

  &_myBlogPrice {
    margin-right: 8px;
    font-weight: bold;
    font-size: 14px;
  }

  &_purchased {
    color: $color-gray-600;
  }

  &_orderCount {
    margin-right: 8px;
    padding: 3px 8px;
    border: 0;
    border-radius: 12px;
    background: $color-green-50;
    color: $color-green-500;
    font-weight: normal;
    font-size: 11px;
    line-height: 13px;

    &-pc {
      display: inline-block;
    }

    &-sp {
      display: none;
    }
  }

  &_img {
    position: relative;
    margin-bottom: 32px;
    //アスペクト比から算出した画像の縦サイズ
    padding-top: calc(100% * 354 / 630);
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }
  }

  &_dividingPayLine {
    margin: 30px 0;
  }

  &_dividingPayLineBorder {
    position: absolute;
    z-index: -1;
    margin-top: 22px;
    width: 630px;
    border: 2px dashed $color-black;
  }

  &_dividingPayLineBlock {
    display: flex;
    margin: 0 auto;
    width: 568px;
    height: 45px;
    border-radius: 6px;
    background: $color-gray-900;
    color: $color-white;
    font-weight: bold;
    font-size: 16px;

    align-items: center;
    justify-content: center;
  }

  &_title {
    display: inline;
    font-weight: bold;
    font-size: 24px;
  }

  &_kind {
    color: $color-gray-600;
    font-size: 10px;

    .coconala-icon {
      margin-left: 8px;
      vertical-align: -1px;
    }
  }

  &_category {
    margin: 8px 0;
    color: $color-gray-600;
    font-size: 12px;
  }

  &_hashTag {
    margin-bottom: 12px;

    a {
      @include link-effect;

      display: inline-block;
      margin: 8px 0 0 8px;
      padding: 8px;
      border: 1px solid $color-gray-200;
      border-radius: 4px;
      background-color: $color-white;
      color: $color-font-base;
      font-size: 12px;
    }
  }

  &_sns {
    display: flex;
    padding: 24px 0 28px;
    border-top: 1px solid $color-gray-200;

    align-items: center;
    justify-content: space-between;
  }

  &_favBtn {
    @include link-effect;

    color: $color-gray-300;
    &-favorited {
      color: $color-pink-500;
    }
  }
  &_favCount {
    font-size: 12px;
  }
}

.c-blogBody {
  margin-bottom: 56px;
  &-paidBlog {
    margin-bottom: 0;
  }

  /deep/ &_text {
    margin: 4px 0;
    font-size: 18px;

    a {
      @include link-effect;
    }
  }
  /deep/ &_insertLink {
    margin: 4px 0;
    font-size: 18px;
  }
  /deep/ &_h2 {
    margin: 20px 0;
    font-weight: bold;
    font-size: 20px;
  }
  /deep/ &_center {
    text-align: center;
  }
  /deep/ &_blockquote {
    margin: 20px 0;
    padding: 24px 12px;
    width: 100%;
    background: $color-gray-100;
    font-size: 14px !important;
    span {
      font-size: 14px !important;
    }
  }
  /deep/ &_img {
    z-index: -1;
    margin-top: 0;
    max-width: 635px;
    text-align: center;
  }

  /deep/ &_youtube {
    position: relative;
    overflow: hidden;
    margin-bottom: 50px;
    padding-bottom: 56.25%;
    width: 100%;
    height: 0;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  /deep/ &_hr {
    padding: 12px 0;

    hr {
      background-color: $color-gray-100;
    }
  }

  /deep/ &_service {
    margin: 20px auto !important;
    width: 232px;
  }

  /deep/ &_widget {
    margin: 20px auto !important;
  }
}

@media (max-width: 630px) {
  .c-blogContent {
    padding: 0 16px;

    &_head {
      margin-bottom: 32px;
    }

    &_ownerRemarkSp {
      margin: 0 24px;
      text-align: center;
    }

    &_orderCount {
      &-pc {
        display: none;
      }
      &-sp {
        display: inline-block;
        margin-right: 0;
        margin-bottom: 16px;
      }
    }

    &_img {
      margin: 0 calc(50% - 50vw) 24px;
      width: 100vw;
    }

    &_title {
      font-size: 20px;
    }

    &_myBlogPrice {
      margin-right: 0;
      font-size: 12px;
    }

    &_dividingPayLine {
      position: relative;
    }

    &_dividingPayLineBorder {
      width: 100%;
    }

    &_dividingPayLineBlock {
      width: 90%;
      font-size: 14px;
    }
  }

  .c-blogBody {
    /deep/ &_h2 {
      font-size: 20px;
    }
    /deep/ &_text {
      font-size: 16px;
    }
    /deep/ &_blockquote {
      font-size: 14px !important;
      span {
        font-size: 14px !important;
      }
    }
    /deep/ &_img {
      max-width: 100%;
    }
  }
}
</style>
